import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Loader2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const Profile = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [bio, setBio] = useState('');
  const [power, setPower] = useState(0);
  const [createdAt, setCreatedAt] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    if (!user) return;

    // Fetch from profiles table (private data)
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching profile:', profileError);
      return;
    }

    // Fetch power points from public_profile_data table
    const { data: publicData, error: publicError } = await supabase
      .from('public_profile_data')
      .select('power')
      .eq('id', user.id)
      .single();

    if (publicError) {
      console.error('Error fetching public profile data:', publicError);
    }

    if (profileData) {
      setFullName(profileData.full_name || '');
      setUsername(profileData.username || '');
      setEmail(profileData.email || '');
      setAvatarUrl(profileData.avatar_url || '');
      setBio(profileData.bio || '');
      setPower(publicData?.power || 0);
      setCreatedAt(profileData.created_at || '');
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    if (!user) return;

    const file = event.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploading(true);

    try {
      // Create image element to process the image
      const img = new Image();
      const reader = new FileReader();

      reader.onload = async (e) => {
        img.src = e.target?.result as string;
        
        img.onload = async () => {
          // Create canvas for cropping to square
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Could not get canvas context');

          // Set canvas size to square (use smaller dimension)
          const size = Math.min(img.width, img.height);
          canvas.width = 512;
          canvas.height = 512;

          // Calculate crop coordinates to center the image
          const sourceX = (img.width - size) / 2;
          const sourceY = (img.height - size) / 2;

          // Draw cropped and resized image
          ctx.drawImage(
            img,
            sourceX, sourceY, size, size,
            0, 0, 512, 512
          );

          // Convert canvas to blob
          canvas.toBlob(async (blob) => {
            if (!blob) throw new Error('Failed to process image');

            const fileExt = file.name.split('.').pop() || 'jpg';
            const fileName = `avatar-${Date.now()}.${fileExt}`;
            const filePath = `${user.id}/${fileName}`;

            // Upload processed image
            const { error: uploadError } = await supabase.storage
              .from('avatars')
              .upload(filePath, blob, { 
                upsert: true,
                contentType: 'image/jpeg'
              });

            if (uploadError) throw uploadError;

            // Get public URL with cache busting
            const { data: urlData } = supabase.storage
              .from('avatars')
              .getPublicUrl(filePath);

            const publicUrlWithCache = `${urlData.publicUrl}?t=${Date.now()}`;

            // Update avatar in both tables
            const { error: updatePublicError } = await supabase
              .from('public_profile_data')
              .update({ avatar_url: publicUrlWithCache })
              .eq('id', user.id);

            if (updatePublicError) throw updatePublicError;

            const { error: updatePrivateError } = await supabase
              .from('profiles')
              .update({ avatar_url: publicUrlWithCache })
              .eq('id', user.id);

            if (updatePrivateError) throw updatePrivateError;

            setAvatarUrl(publicUrlWithCache);
            toast.success('Profile photo updated!');
            setUploading(false);
          }, 'image/jpeg', 0.9);
        };

        img.onerror = () => {
          toast.error('Failed to load image');
          setUploading(false);
        };
      };

      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error: any) {
      toast.error(error.message || 'Error uploading photo');
      setUploading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);

    // Update private data (full_name) in profiles table
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        full_name: fullName,
      })
      .eq('id', user.id);

    if (profileError) {
      toast.error(profileError.message || 'Error updating profile');
      setLoading(false);
      return;
    }

    // Update public data (bio) in public_profile_data table
    const { error: publicError } = await supabase
      .from('public_profile_data')
      .update({
        bio: bio,
      })
      .eq('id', user.id);

    if (publicError) {
      toast.error(publicError.message || 'Error updating public profile');
    } else {
      toast.success('Profile updated successfully!');
    }

    setLoading(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <>
      <SEO
        title="My Profile - Crown Law Library"
        description="Manage your profile information"
      />
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-20">
          <Card className="max-w-2xl mx-auto shadow-2xl border-2 border-gold/40">
            <CardHeader>
              <CardTitle className="text-[1.09rem] font-rockwell text-center">
                Edit My Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar Upload */}
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <Avatar className="h-16 w-16 border-2 border-gold/40 shadow-lg">
                    <AvatarImage 
                      src={avatarUrl} 
                      className="object-cover"
                      key={avatarUrl}
                    />
                    <AvatarFallback className="text-xl bg-muted">
                      {username?.[0]?.toUpperCase() || fullName?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <label
                    htmlFor="avatar-upload"
                    className="absolute inset-0 cursor-pointer rounded-full hover:bg-black/10 transition-colors flex items-center justify-center"
                  >
                    {uploading && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={uploading}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 py-4 border-y border-gold/20 mb-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">{power}</p>
                  <p className="text-sm text-muted-foreground">Power Points</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Earned from comments and reactions
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-muted-foreground">Member Since</p>
                  <p className="text-base font-semibold">
                    {createdAt ? new Date(createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Profile Form */}
              <form onSubmit={handleUpdateProfile} className="space-y-0">
                <div className="flex items-center gap-2 md:gap-6 py-3.5 border-b border-border/50">
                  <Label htmlFor="fullName" className="w-20 md:w-28 text-sm font-normal text-foreground shrink-0">Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="flex-1 border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>

                <div className="flex items-center gap-2 md:gap-6 py-3.5 border-b border-border/50">
                  <Label htmlFor="username" className="w-20 md:w-28 text-sm font-normal text-foreground shrink-0">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    disabled
                    className="flex-1 border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-muted-foreground"
                  />
                </div>

                <div className="flex items-center gap-2 md:gap-6 py-3.5 border-b border-border/50">
                  <Label htmlFor="email" className="w-20 md:w-28 text-sm font-normal text-foreground shrink-0">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    disabled
                    className="flex-1 border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-muted-foreground"
                  />
                </div>

                <div className="flex items-start gap-2 md:gap-6 py-3.5 border-b border-border/50">
                  <Label htmlFor="bio" className="w-20 md:w-28 text-sm font-normal text-foreground pt-0 shrink-0">Bio</Label>
                  <div className="flex-1">
                    <Textarea
                      id="bio"
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Bio"
                      className="min-h-[60px] border-0 bg-transparent px-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
                      maxLength={500}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {bio.length}/500 characters
                    </p>
                  </div>
                </div>

                <div className="pt-6 border-t border-gold/20">
                  <button 
                    type="submit"
                    className="text-foreground hover:underline cursor-pointer" 
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </form>

              {/* Logout Button */}
              <div className="pt-3">
                <button 
                  className="text-destructive hover:underline cursor-pointer" 
                  onClick={handleSignOut}
                >
                  Logout
                </button>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Profile;
